import React from 'react'
import 'bootstrap/dist/css/bootstrap.min.css'
import Parse from 'parse'
import { Route } from 'react-router-dom'
import { getSourceMeta } from './swimCoachConversationSource'
import SwimCoachModelBadges from './SwimCoachModelBadges'
import { MODEL_FIELD, MODEL_NOTE, modelsByConversation } from './swimCoachModel'
import {
  ROLLUP_FIELD,
  SAMPLING_NOTE,
  SCORE_TITLE,
  GOOD_SCORE,
  formatScore,
  readRollup,
  scoreBadgeClass
} from './swimCoachRating'

const PAGE_SIZE = 100
const WARN_REVIEW_LIMIT = 10000
// Coach replies carrying a model, across one page of conversations. Generous
// for PAGE_SIZE conversations; past it, the newest replies across the whole
// page drop first (see loadModels for how that is surfaced).
const MODEL_ROW_LIMIT = 10000

const STATUS_FILTERS = [
  { value: 'all', label: 'All' },
  { value: 'locked', label: 'Locked' },
  { value: 'warned', label: 'Warned' },
  { value: 'clean', label: 'Clean' }
]

// The score options name their threshold. "Weak" alone was ambiguous against
// the badge bands — it read as the amber 0.60-0.79 band while the query
// returned only the red one below 0.60. These match GOOD_SCORE, so the filter
// means "everything the badges do not colour green", which is what triage
// actually wants.
const RATING_FILTERS = [
  { value: 'all', label: 'All' },
  { value: 'gated', label: 'Gated' },
  { value: 'weakCoach', label: 'Coach score < 0.80' },
  { value: 'weakCx', label: 'CX score < 0.80' },
  { value: 'rated', label: 'Rated' },
  { value: 'unrated', label: 'Not rated' }
]

const SORT_OPTIONS = [
  { value: 'recent', label: 'Last message' },
  { value: 'worstCoach', label: 'Worst coach score' },
  { value: 'worstCx', label: 'Worst CX score' }
]

// Sorting ascending by a score would otherwise lead with every unrated
// conversation, since a missing field sorts below any number. Each score sort
// therefore restricts to rated conversations.
const SCORE_SORTS = {
  worstCoach: ROLLUP_FIELD.AVG_COACH_SCORE,
  worstCx: ROLLUP_FIELD.AVG_CX_SCORE
}

function deriveStatus (row) {
  if (row.lockedAt) return 'locked'
  if (row.warnCount === null) return 'unknown'
  if (row.warnCount > 0) return 'warned'
  return 'clean'
}

function renderSourceLink (entityType, entityId) {
  const meta = getSourceMeta(entityType)
  if (!meta || !entityId) {
    return (<span className="text-muted">—</span>)
  }
  return (
    <a href={'/#' + meta.path + entityId}>
      {meta.label}
    </a>
  )
}

function renderRating (rollup) {
  if (!rollup) {
    return (<span className="text-muted" title={SAMPLING_NOTE}>Not rated</span>)
  }
  const { avgCoachScore, avgCxScore, evaluatedTurns, gatedTurns } = rollup
  // A gated turn zeroes that turn's coach score outright, so an average with
  // gates in it is not a "slightly worse" average — flag it separately rather
  // than letting the number stand alone.
  const gatedTitle = gatedTurns
    ? gatedTurns + ' of ' + evaluatedTurns +
      ' rated responses tripped a hard-failure gate (fabrication, unsafe ' +
      'prescription, medical advice, false feature claim, or a claimed write ' +
      'that never happened).'
    : ''
  return (
    <div>
      <span
        className={'badge me-1 ' + scoreBadgeClass(avgCoachScore, false)}
        title={SCORE_TITLE.COACH}
      >
        {'Coach ' + formatScore(avgCoachScore)}
      </span>
      <span
        className={'badge ' + scoreBadgeClass(avgCxScore, false)}
        title={SCORE_TITLE.CX}
      >
        {'CX ' + formatScore(avgCxScore)}
      </span>
      <div className="small text-muted mt-1">
        {evaluatedTurns + (evaluatedTurns === 1 ? ' turn' : ' turns')}
        {gatedTurns > 0 && (
          <span className="badge text-bg-danger ms-1" title={gatedTitle}>
            {gatedTurns + ' gated'}
          </span>
        )}
      </div>
    </div>
  )
}

function renderStatusBadge (row) {
  const status = deriveStatus(row)
  if (status === 'locked') {
    const label = row.lockCategory ? 'Locked · ' + row.lockCategory : 'Locked'
    return (<span className="badge text-bg-danger" title={row.lockMessage || ''}>{label}</span>)
  }
  if (status === 'warned') {
    return (<span className="badge text-bg-warning">{'Warned · ' + row.warnCount}</span>)
  }
  if (status === 'unknown') {
    return (<span className="badge text-bg-secondary" title="Safety reviews could not be loaded">?</span>)
  }
  return (<span className="badge text-bg-light">Clean</span>)
}

export default class SwimCoachConversationListComponent extends React.Component {
  constructor (props) {
    super(props)
    this.state = {
      conversations: null,
      statusFilter: 'all',
      ratingFilter: 'all',
      sortBy: 'recent',
      loadError: null,
      warnCountsError: null,
      warnTruncated: false
    }
    this.activeRequestId = 0
    this.loadContent()
  }

  loadContent = async () => {
    // Tag this load so a response from a superseded filter selection can't
    // overwrite the latest results if requests resolve out of order.
    const requestId = ++this.activeRequestId
    const applyState = (next) => {
      if (requestId === this.activeRequestId) this.setState(next)
    }

    const { statusFilter, ratingFilter, sortBy } = this.state
    const needsWarnIds = statusFilter === 'warned' || statusFilter === 'clean'

    // Warn status lives on SwimCoachSafetyReview, so load the full warn map up
    // front. It powers both the status badges and the warned/clean filters.
    const { counts: warnCounts, ids: warnedIds, error: warnError, truncated } = await this.loadWarnData()

    // The warned/clean filters need the warn id set to build the query, so we
    // can't proceed if it failed to load.
    if (needsWarnIds && warnError) {
      applyState({ conversations: [], warnCountsError: warnError, warnTruncated: false })
      return
    }

    let conversations
    try {
      const conversationQuery = this.buildConversationQuery({
        statusFilter, ratingFilter, sortBy, warnedIds
      })
      conversations = await conversationQuery.find()
    } catch (error) {
      console.error('Error loading conversations:', error)
      applyState({ loadError: error.message })
      return
    }

    // Started before the counts so the two load in parallel.
    const modelsPromise = this.loadModels(conversations)
    const counts = await Promise.all(
      conversations.map(async (conversation) => {
        try {
          const messageQuery = new Parse.Query('SwimCoachMessage')
          messageQuery.equalTo('conversation', conversation)
          return await messageQuery.count()
        } catch (error) {
          console.error('Error counting messages for', conversation.id, error)
          return null
        }
      })
    )
    const modelMap = await modelsPromise

    const rows = conversations.map((conversation, index) => ({
      id: conversation.id,
      userId: conversation.get('user') ? conversation.get('user').id : null,
      lastMessageAt: conversation.get('lastMessageAt'),
      messageCount: counts[index],
      models: modelMap ? modelMap[conversation.id] : null,
      lockedAt: conversation.get('lockedAt') || null,
      lockCategory: conversation.get('lockCategory') || null,
      lockMessage: conversation.get('lockMessage') || null,
      entityType: conversation.get('entityType') || null,
      entityId: conversation.get('entityId') || null,
      warnCount: warnCounts ? (warnCounts[conversation.id] || 0) : null,
      // Denormalized by the backend after each evaluation, so no extra query.
      rating: readRollup(conversation)
    }))

    applyState({ conversations: rows, warnCountsError: warnError, warnTruncated: truncated })
  }

  // Builds the backend query for the active selections so filtering and sorting
  // happen server-side across all conversations, not just the loaded page.
  buildConversationQuery = ({ statusFilter, ratingFilter, sortBy, warnedIds }) => {
    const query = new Parse.Query('SwimCoachConversation')
    query.limit(PAGE_SIZE)
    this.applySort(query, sortBy)
    if (statusFilter === 'locked') {
      query.exists('lockedAt')
    } else if (statusFilter === 'warned') {
      query.doesNotExist('lockedAt')
      query.containedIn('objectId', warnedIds)
    } else if (statusFilter === 'clean') {
      query.doesNotExist('lockedAt')
      query.notContainedIn('objectId', warnedIds)
    }
    this.applyRatingFilter(query, ratingFilter)
    return query
  }

  applySort = (query, sortBy) => {
    const scoreField = SCORE_SORTS[sortBy]
    if (!scoreField) {
      query.descending('lastMessageAt')
      return
    }
    // Worst first, and only among conversations that actually carry a score.
    query.exists(scoreField)
    query.ascending(scoreField)
  }

  applyRatingFilter = (query, ratingFilter) => {
    if (ratingFilter === 'gated') {
      query.greaterThan(ROLLUP_FIELD.GATED_TURNS, 0)
    } else if (ratingFilter === 'weakCoach') {
      query.lessThan(ROLLUP_FIELD.AVG_COACH_SCORE, GOOD_SCORE)
    } else if (ratingFilter === 'weakCx') {
      query.lessThan(ROLLUP_FIELD.AVG_CX_SCORE, GOOD_SCORE)
    } else if (ratingFilter === 'rated') {
      query.exists(ROLLUP_FIELD.EVALUATED_TURNS)
    } else if (ratingFilter === 'unrated') {
      query.doesNotExist(ROLLUP_FIELD.EVALUATED_TURNS)
    }
  }

  // The model lives on each coach reply, not on the conversation, so read the
  // whole page's replies in one query rather than one per row. Returns an
  // entry for every conversation, or null on failure, so the column shows "?"
  // instead of claiming none was recorded.
  loadModels = async (conversations) => {
    if (!conversations.length) {
      return {}
    }
    try {
      const query = new Parse.Query('SwimCoachMessage')
      query.containedIn('conversation', conversations)
      query.exists(MODEL_FIELD)
      query.select('conversation', MODEL_FIELD)
      query.ascending('createdAt')
      query.limit(MODEL_ROW_LIMIT)
      const replies = await query.find()
      const found = modelsByConversation(replies)
      // Hitting the cap cuts the newest replies across the whole page, which
      // can drop every reply of the most recently active conversations. A
      // conversation left with no model is then unknown, not "none recorded".
      const truncated = replies.length >= MODEL_ROW_LIMIT
      const models = {}
      conversations.forEach((conversation) => {
        models[conversation.id] =
          found[conversation.id] || (truncated ? null : [])
      })
      return models
    } catch (error) {
      console.error('Error loading conversation models:', error)
      return null
    }
  }

  // Loads every "warn" safety review and aggregates it into per-conversation
  // counts plus the set of warned conversation ids used by the status filter.
  loadWarnData = async () => {
    try {
      const reviewQuery = new Parse.Query('SwimCoachSafetyReview')
      reviewQuery.equalTo('status', 'warn')
      reviewQuery.limit(WARN_REVIEW_LIMIT)
      const reviews = await reviewQuery.find()
      const counts = {}
      reviews.forEach((review) => {
        const conv = review.get('conversation')
        if (!conv) return
        counts[conv.id] = (counts[conv.id] || 0) + 1
      })
      // Hitting the cap means the warn map may be incomplete, which makes
      // warned/clean filtering and warn counts unreliable — flag it so the UI
      // can warn rather than silently misrepresent status.
      const truncated = reviews.length >= WARN_REVIEW_LIMIT
      return { counts, ids: Object.keys(counts), error: null, truncated }
    } catch (error) {
      console.error('Error loading safety reviews:', error)
      return { counts: null, ids: [], error: error.message, truncated: false }
    }
  }

  // One handler for all three selects: each re-queries the backend so the
  // selection applies across every conversation rather than only the page
  // currently loaded in memory.
  handleSelectionChange = (event) => {
    const { name, value } = event.target
    this.setState(
      {
        [name]: value,
        conversations: null,
        loadError: null,
        warnCountsError: null,
        warnTruncated: false
      },
      this.loadContent
    )
  }

  numberOfResultsText = () => {
    const { conversations, loadError } = this.state
    if (loadError) return null
    if (!conversations) return (<i>Loading...</i>)
    if (conversations.length === PAGE_SIZE) {
      return (<i>Showing first {PAGE_SIZE} (more may exist)</i>)
    }
    return (<i>{conversations.length} loaded</i>)
  }

  renderErrorBanner = () => {
    const { loadError, warnCountsError, warnTruncated } = this.state
    if (loadError) {
      return (
        <div className="alert alert-danger" role="alert">
          <b>Error loading conversations:</b> {loadError}
        </div>
      )
    }
    if (warnCountsError) {
      return (
        <div className="alert alert-warning" role="alert">
          <b>Safety review status unavailable:</b> {warnCountsError}.
          {' '}Lock state is still shown; warn counts appear as &quot;?&quot;.
        </div>
      )
    }
    if (warnTruncated) {
      return (
        <div className="alert alert-warning" role="alert">
          <b>Showing the first {WARN_REVIEW_LIMIT} safety reviews.</b>
          {' '}Warn/Clean filtering and warn counts may be incomplete.
        </div>
      )
    }
    return null
  }

  renderSelect = (id, label, options, value) => {
    return (
      <div className="d-flex align-items-center me-4">
        <label htmlFor={id} className="me-2">{label}</label>
        <select
          id={id}
          name={id}
          className="form-select form-select-sm w-auto"
          value={value}
          onChange={this.handleSelectionChange}
        >
          {options.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      </div>
    )
  }

  renderFilterControls = () => {
    const { statusFilter, ratingFilter, sortBy } = this.state
    return (
      <div className="d-flex align-items-center flex-wrap mb-3">
        {this.renderSelect(
          'statusFilter', 'Status:', STATUS_FILTERS, statusFilter
        )}
        {this.renderSelect(
          'ratingFilter', 'Rating:', RATING_FILTERS, ratingFilter
        )}
        {this.renderSelect('sortBy', 'Sort:', SORT_OPTIONS, sortBy)}
      </div>
    )
  }

  // The sort arrow belongs on whichever column is actually sorted, otherwise it
  // keeps claiming Last Message while the rows are ordered by score.
  renderLastMessageHeader = () => {
    return this.state.sortBy === 'recent' ? 'Last Message \u25be' : 'Last Message'
  }

  renderTableHeader = () => {
    return (
      <tr key={'0-header'} className="data">
        <th className="bg-primary" key={'0h'}>User ID</th>
        <th className="bg-primary" key={'1h'}>Status</th>
        <th className="bg-primary" key={'2h'} title={MODEL_NOTE}>Model</th>
        <th className="bg-primary" key={'3h'} title={SAMPLING_NOTE}>Rating</th>
        <th className="bg-primary" key={'4h'}>Messages</th>
        <th className="bg-primary" key={'5h'}>Source</th>
        <th className="bg-primary" key={'6h'}>{this.renderLastMessageHeader()}</th>
        <th className="bg-primary" key={'7h'}></th>
      </tr>
    )
  }

  renderTable = () => {
    const rows = this.state.conversations
    if (!rows) return null
    if (!rows.length) {
      return (
        <tr className="data">
          <td colSpan="8">
            <span className="text-muted">No conversations found</span>
          </td>
        </tr>
      )
    }
    return rows.map((row, index) => {
      return (
        <tr key={index} className="data">
          <td>
            {row.userId
              ? (<a href={'/#/members/' + row.userId}><code>{row.userId}</code></a>)
              : (<code>(unknown)</code>)}
          </td>
          <td>{renderStatusBadge(row)}</td>
          <td><SwimCoachModelBadges models={row.models} /></td>
          <td>{renderRating(row.rating)}</td>
          <td>{row.messageCount}</td>
          <td>{renderSourceLink(row.entityType, row.entityId)}</td>
          <td>
            {row.lastMessageAt ? row.lastMessageAt.toLocaleString() : ''}
          </td>
          <td>
            <a href={'/#/swimCoachConversations/' + row.id}>Open →</a>
          </td>
        </tr>
      )
    })
  }

  render = () => {
    return (
      <div>
        <Route path="/swimCoachConversations">
          <div className="container">
            <br/>
            <div>
              <h1 id="title">Swim Coach Conversations</h1>
              <p className="text-muted">
                User IDs only — names and emails are hidden for privacy.
                Use the Status filter to spot-check warned and locked conversations,
                and the Rating filter to find conversations an evaluator scored
                badly. {SAMPLING_NOTE}
              </p>
            </div>
            <br/>
            {this.renderErrorBanner()}
            {this.renderFilterControls()}
            <table id="swimcoachconversations" className="data">
              <tbody>
                {this.renderTableHeader()}
                {this.renderTable()}
              </tbody>
            </table>
            <br/>
            {this.numberOfResultsText()}
            <br/>
          </div>
        </Route>
      </div>
    )
  }
}
